import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Form } from 'react-bootstrap';
import Select from 'react-select';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { CreateUpdateProfileCategoryYearDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { validatePCYear } from '../../../common/pokCore/contexts/ProfileCategoryContext';
import FormRow from '../../../app/components/FormRow';
import { useTranslation } from '../../../translations';
import BooleanDropdown from '../../../app/components/BooleanDropdown';
import { usePokCore } from '../../../common/hooks/usePokCore';
import LastEditorInfo from '../../../app/components/LastEditorInfo';

interface ProfileCategoryYearModalEditorProps {
  id?: string;
  profileCategoryYear: CreateUpdateProfileCategoryYearDto;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    profileCategoryYear: CreateUpdateProfileCategoryYearDto,
    id?: string,
  ) => void;
}

export const ProfileCategoryYearModalEditor: React.FC<
  ProfileCategoryYearModalEditorProps
> = props => {
  const [year, setYear] = useState<number>();
  const [yearsOptions, setYearsOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const [profileCategoryYear, setProfileCategoryYear] = useState(
    props.profileCategoryYear,
  );
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const { t, tk } = useTranslation('administrationLng');
  const pok = usePokCore();

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const options = [];
    for (let year: number = 2024; year <= currentYear + 2; year++) {
      options.push({
        value: year,
        label: String(year),
      });
    }
    setYearsOptions(options);
    setYear(profileCategoryYear?.year ?? currentYear);
  }, [profileCategoryYear]);

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setProfileCategoryYear(props.profileCategoryYear);
    }
  }, [props.profileCategoryYear, props.show]);

  const addEditProfileCategoryYear = async () => {
    const status = await validatePCYear(profileCategoryYear);
    if (status.valid) {
      props.onCompleted(profileCategoryYear, props.id);
    } else {
      setSaveErrors(status.errors);
      setShowSaveErrors(true);
    }
  };

  const propertyChange = (obj: Partial<CreateUpdateProfileCategoryYearDto>) => {
    setProfileCategoryYear({ ...profileCategoryYear, ...obj });
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {props.id
            ? t(tk.administration.profileCategoryEdit)
            : t(tk.administration.profileCategoryAdd)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="d-grid gap-4 mb-3">
          <FormRow controlId="yaer" label={t(tk.common.year)}>
            <Select
              value={yearsOptions.find(({ value }) => value === year)}
              options={yearsOptions}
              onChange={option => {
                setYear(Number(option?.value));
                propertyChange({ year: option?.value });
              }}
            />
          </FormRow>
          <FormRow
            controlId="internal"
            label={t(tk.administration.profileCategoryAccountsInternal)}
          >
            <BooleanDropdown
              //readOnly={props.readOnly}
              value={profileCategoryYear.internal}
              onChange={value => propertyChange?.({ internal: value })}
              canBeUndefined={true}
            />
          </FormRow>
          <FormRow
            controlId="salesFinancialAccount"
            label={t(tk.administration.salesFinancialAccount)}
          >
            <Form.Control
              type="text"
              value={profileCategoryYear.salesFinancialAccount || ''}
              onChange={e =>
                propertyChange({ salesFinancialAccount: e.target.value })
              }
            />
          </FormRow>
          <FormRow
            controlId="purchasesFinancialAccount"
            label={t(tk.administration.purchasesFinancialAccount)}
          >
            <Form.Control
              type="text"
              value={profileCategoryYear.purchasesFinancialAccount || ''}
              onChange={e =>
                propertyChange?.({ purchasesFinancialAccount: e.target.value })
              }
            />
          </FormRow>
          <FormRow
            controlId="commissionFinancialAccount"
            label={t(tk.administration.commissionsFinancialAccount)}
          >
            <Form.Control
              type="text"
              value={profileCategoryYear.commissionFinancialAccount || ''}
              onChange={e =>
                propertyChange?.({ commissionFinancialAccount: e.target.value })
              }
            />
          </FormRow>
          <FormRow controlId="active" label={t(tk.common.activeHe)}>
            <Form.Check
              type="checkbox"
              className="pt-2"
              checked={profileCategoryYear?.active ?? true}
              onChange={e => propertyChange({ active: e.target.checked })}
            />
          </FormRow>
        </Form>
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
        <div className="d-flex justify-content-end">
          {props.profileCategoryYear.id && (
            <LastEditorInfo
              id={props.profileCategoryYear.id}
              method={pok.profileCategories.getYearLastEditor}
            />
          )}
          <Button variant="outline-secondary" onClick={props.handleClose}>
            {t(tk.button.cancel)}
          </Button>
          <Button
            className="ms-2"
            variant="primary"
            onClick={addEditProfileCategoryYear}
          >
            {props.id ? t(tk.button.change) : t(tk.button.add)}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};
